export interface FileValidationResult {
  isValid: boolean
  errors: string[]
}

// Magic numbers
const FILE_SIGNATURES = {
  'image/jpeg': [0xff, 0xd8, 0xff],
  'image/png': [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  'image/gif': [0x47, 0x49, 0x46, 0x38],
  'image/webp': [0x52, 0x49, 0x46, 0x46],
  'application/pdf': [0x25, 0x50, 0x44, 0x46],
  'application/msword': [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    0x50, 0x4b, 0x03, 0x04,
  ],
} as const

// Allowed file types with specific MIME types
const ALLOWED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
} as const

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_FILENAME_LENGTH = 255

/**
 * Removing dangerous characters
 */
export const sanitizeFileName = (name: string): string => {
  return name
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '')
    .replace(/^\.+/, '')
    .replace(/\.+$/, '')
    .substring(0, MAX_FILENAME_LENGTH)
    .trim()
}

/**
 * Validates file size
 */
const validateFileSize = (file: File): string[] => {
  const errors: string[] = []

  if (file.size > MAX_FILE_SIZE) {
    errors.push(
      `Arquivo muito grande. Máximo permitido: ${
        MAX_FILE_SIZE / (1024 * 1024)
      }MB`,
    )
  }

  if (file.size === 0) {
    errors.push('Arquivo vazio não é permitido')
  }

  return errors
}

/**
 * Validates file name
 */
const validateFileName = (file: File): string[] => {
  const errors: string[] = []
  const sanitized = sanitizeFileName(file.name)

  if (sanitized !== file.name) {
    errors.push('Nome do arquivo contém caracteres inválidos')
  }

  if (file.name.length === 0) {
    errors.push('Nome do arquivo é obrigatório')
  }

  if (file.name.length > MAX_FILENAME_LENGTH) {
    errors.push(
      `Nome do arquivo muito longo. Máximo: ${MAX_FILENAME_LENGTH} caracteres`,
    )
  }

  return errors
}

/**
 * Validates MIME type against allowed types
 */
const validateMimeType = (file: File): string[] => {
  const errors: string[] = []

  if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
    errors.push(`Tipo de arquivo não permitido: ${file.type}`)
  }

  return errors
}

/**
 * Validates file extension against MIME type
 */
const validateFileExtension = (file: File): string[] => {
  const errors: string[] = []
  const extension = '.' + file.name.split('.').pop()?.toLowerCase()

  if (!extension || extension === '.') {
    errors.push('Arquivo deve ter uma extensão válida')
    return errors
  }

  const allowedExtensions =
    ALLOWED_FILE_TYPES[file.type as keyof typeof ALLOWED_FILE_TYPES]
  if (!allowedExtensions) {
    errors.push(`Tipo de arquivo não suportado: ${file.type}`)
    return errors
  }

  if (!allowedExtensions.includes(extension as never)) {
    errors.push(
      `Extensão ${extension} não é compatível com o tipo ${file.type}`,
    )
  }

  return errors
}

/**
 * Validates file signature (magic numbers) to prevent MIME spoofing
 */
const validateFileSignature = async (file: File): Promise<string[]> => {
  const errors: string[] = []

  try {
    const buffer = await file.slice(0, 8).arrayBuffer()
    const bytes = new Uint8Array(buffer)

    const expectedSignature =
      FILE_SIGNATURES[file.type as keyof typeof FILE_SIGNATURES]
    if (!expectedSignature) {
      errors.push(`Tipo de arquivo não suportado para validação: ${file.type}`)
      return errors
    }

    let isValid = true
    for (let i = 0; i < expectedSignature.length; i++) {
      if (bytes[i] !== expectedSignature[i]) {
        isValid = false
        break
      }
    }

    if (!isValid) {
      errors.push(`Assinatura do arquivo não corresponde ao tipo ${file.type}`)
    }
  } catch (error) {
    errors.push('Erro ao validar assinatura do arquivo')
  }

  return errors
}

export const validateFile = async (
  file: File,
): Promise<FileValidationResult> => {
  const errors: string[] = []

  errors.push(...validateFileSize(file))
  errors.push(...validateFileName(file))
  errors.push(...validateMimeType(file))
  errors.push(...validateFileExtension(file))

  // file signature
  if (errors.length === 0) {
    const signatureErrors = await validateFileSignature(file)
    errors.push(...signatureErrors)
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

export const validateFiles = async (
  files: File[],
): Promise<{
  validFiles: File[]
  invalidFiles: { file: File; errors: string[] }[]
}> => {
  const validFiles: File[] = []
  const invalidFiles: { file: File; errors: string[] }[] = []

  for (const file of files) {
    const result = await validateFile(file)
    if (result.isValid) {
      validFiles.push(file)
    } else {
      invalidFiles.push({ file, errors: result.errors })
    }
  }

  return { validFiles, invalidFiles }
}

export const getAllowedFileTypes = (): string[] => {
  return Object.keys(ALLOWED_FILE_TYPES)
}

// export const getAllowedExtensions = (): string[] => {
//   const extensions: string[] = [];
//   Object.values(ALLOWED_FILE_TYPES).forEach((exts) => {
//     extensions.push(...exts);
//   });
//   return [...new Set(extensions)]; // Remove duplicates
// };
